import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ReactApexChart from 'react-apexcharts';

import { BreadcrumbHeader, StatCard, DataTable, StatusBadge, EmptyState } from '../../components/shared';
import { partOrders, shipments, vendorShipments, inventoryAlerts } from '../../services/dataClient';

// ─── Status colors (matches StatusBadge) ─────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";const STATUS_COLORS = {
  Open: '#1565C0',
  Fulfilled: '#00695C',
  Shipped: '#4527A0',
  Completed: '#2E7D32',
  Active: '#1565C0',
  'In Transit': '#4527A0',
  Delivered: '#2E7D32'
};

// ─── Section card wrapper ────────────────────────────────────────────────────
function SectionCard({ title, viewAllPath, children }) {
  const navigate = useNavigate();
  return (
    <ContentCard
      sx={{ overflow: 'hidden'
      }}>
      
      <Box
        sx={{ display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: '14px',
          borderBottom: '1px solid var(--grey-200)'
        }}>
        
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
          {title}
        </Typography>
        {viewAllPath &&
        <Typography
          onClick={() => navigate(viewAllPath)}
          sx={{ fontSize: '0.8125rem',
            color: '#009688',
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' }
          }}>
          
            View All
          </Typography>
        }
      </Box>
      {children}
    </ContentCard>);

}

// ─── Chart + Table side-by-side layout ───────────────────────────────────────
function ChartTableRow({ chart, table }) {
  return (
    <Box
      sx={{ display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        borderTop: '1px solid var(--grey-200)'
      }}>
      
      {/* Chart pane */}
      <Box
        sx={{ width: { xs: '100%', md: 320 },
          flexShrink: 0,
          height: 220,
          borderRight: { md: '1px solid var(--grey-200)' },
          borderBottom: { xs: '1px solid var(--grey-200)', md: 'none' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1
        }}>
        
        {chart}
      </Box>

      {/* Table pane */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>{table}</Box>
    </Box>);

}

// ─── Column definitions ──────────────────────────────────────────────────────
const partOrderColumns = [
{ field: 'ticketNumber', headerName: 'Ticket #' },
{ field: 'partNumber', headerName: 'Part #' },
{ field: 'customerName', headerName: 'Customer' },
{ field: 'status', headerName: 'Status', renderCell: (row) => <StatusBadge status={row.status} /> },
{ field: 'slaDueDate', headerName: 'SLA Due Date' },
{ field: 'source', headerName: 'Source' }];

const shipmentColumns = [
{ field: 'shipmentNumber', headerName: 'Shipment #' },
{ field: 'originLocation', headerName: 'Origin' },
{ field: 'customerName', headerName: 'Destination' },
{ field: 'carrier', headerName: 'Carrier' },
{ field: 'trackingNumber', headerName: 'Tracking #' },
{ field: 'status', headerName: 'Status', renderCell: (row) => <StatusBadge status={row.status} /> }];

const alertColumns = [
{ field: 'partNumber', headerName: 'Part #' },
{ field: 'locationName', headerName: 'Location' },
{ field: 'alertType', headerName: 'Alert Type', renderCell: (row) => <StatusBadge status={row.alertType} /> },
{ field: 'triggeredDate', headerName: 'Triggered Date' }];

// ─── ApexCharts shared defaults ───────────────────────────────────────────────
const baseChartOptions = {
  chart: { toolbar: { show: false }, animations: { enabled: false }, background: 'transparent' },
  dataLabels: { enabled: false },
  grid: { borderColor: '#e3e8ef', strokeDashArray: 4, yaxis: { lines: { show: true } }, xaxis: { lines: { show: false } } },
  tooltip: { theme: 'light' }
};

// ─── Page ────────────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const navigate = useNavigate();

  // ── Stat counts
  const openPartOrdersCount = useMemo(() => partOrders.filter((o) => o.status === 'Open').length, []);
  const fulfilledPartOrdersCount = useMemo(() => partOrders.filter((o) => o.status === 'Fulfilled').length, []);
  const activeShipmentsCount = useMemo(
    () => shipments.filter((s) => s.status === 'Active' || s.status === 'In Transit').length, []
  );
  const vendorInTransitCount = useMemo(
    () => vendorShipments.filter((v) => v.status === 'In Transit').length, []
  );
  const alertsCount = inventoryAlerts.length;

  // ── Table data
  const recentPartOrders = useMemo(
    () => [...partOrders].sort((a, b) => a.slaDueDate > b.slaDueDate ? 1 : -1).slice(0, 5), []
  );
  const activeShipments = useMemo(
    () => shipments.filter((s) => s.status === 'Active' || s.status === 'In Transit').slice(0, 5), []
  );

  // ── Chart data: Part Orders by status (bar)
  const partOrderStatuses = ['Open', 'Fulfilled', 'Shipped', 'Completed'];
  const partOrderCounts = useMemo(
    () => partOrderStatuses.map((s) => partOrders.filter((o) => o.status === s).length),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const partOrderBarOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'bar' },
    xaxis: { categories: partOrderStatuses, labels: { style: { fontSize: '11px', colors: '#607d8b' } } },
    yaxis: { labels: { style: { fontSize: '11px', colors: '#607d8b' } }, tickAmount: Math.max(...partOrderCounts) },
    colors: partOrderStatuses.map((s) => STATUS_COLORS[s]),
    plotOptions: { bar: { distributed: true, borderRadius: 4, columnWidth: '50%' } },
    legend: { show: false }
  };
  const partOrderBarSeries = [{ name: 'Orders', data: partOrderCounts }];

  // ── Chart data: Shipments by status (donut)
  const shipmentStatuses = ['Active', 'In Transit', 'Delivered'];
  const shipmentCounts = useMemo(
    () => shipmentStatuses.map((s) => shipments.filter((sh) => sh.status === s).length),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const shipmentDonutOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'donut' },
    labels: shipmentStatuses,
    colors: shipmentStatuses.map((s) => STATUS_COLORS[s]),
    legend: {
      show: true,
      position: 'right',
      fontSize: '12px',
      labels: { colors: '#607d8b' },
      itemMargin: { vertical: 4 }
    },
    plotOptions: { pie: { donut: { size: '65%' } } }
  };

  // ── Chart data: Alerts by location (horizontal bar)
  const alertLocations = useMemo(() => {
    const counts = {};
    inventoryAlerts.forEach((a) => {
      counts[a.locationName] = (counts[a.locationName] || 0) + 1;
    });
    return Object.entries(counts).map(([loc, count]) => ({ loc, count }));
  }, []);
  const alertBarOptions = {
    ...baseChartOptions,
    chart: { ...baseChartOptions.chart, type: 'bar' },
    xaxis: { categories: alertLocations.map((x) => x.loc), labels: { style: { fontSize: '10px', colors: '#607d8b' } } },
    yaxis: { labels: { style: { fontSize: '11px', colors: '#607d8b' } }, tickAmount: Math.max(...alertLocations.map((x) => x.count), 1) },
    colors: ['#E65100'],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%', horizontal: true } },
    legend: { show: false }
  };
  const alertBarSeries = [{ name: 'Alerts', data: alertLocations.map((x) => x.count) }];

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <BreadcrumbHeader
        title="Dashboard"
        crumbs={[{ label: 'Dashboard' }]} />

      {/* ── Stat cards ── */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <StatCard label="Open Part Orders" value={openPartOrdersCount} linkTo="/orders/parts" />
        <StatCard label="Fulfilled (Awaiting Shipment)" value={fulfilledPartOrdersCount} linkTo="/orders/parts" />
        <StatCard label="Active Shipments" value={activeShipmentsCount} linkTo="/shipments/dispatch" />
        <StatCard label="Replenishment In Transit" value={vendorInTransitCount} linkTo="/shipments/replenishment-shipments" />
        <StatCard label="Inventory Alerts" value={alertsCount} linkTo="/inventory" />
      </Box>

      {/* ── Recent Part Orders ── */}
      <SectionCard title="Recent Part Orders" viewAllPath="/orders/parts">
        {recentPartOrders.length === 0 ?
        <EmptyState title="No part orders" description="Part orders will appear here once created." /> :

        <ChartTableRow
          chart={
          <ReactApexChart
            type="bar"
            options={partOrderBarOptions}
            series={partOrderBarSeries}
            height={200}
            width="100%" />

          }
          table={
          <DataTable
            columns={partOrderColumns}
            data={recentPartOrders}
            onRowClick={(row) => navigate(`/orders/parts/${row.id}`)} />

          } />

        }
      </SectionCard>

      {/* ── Active Shipments ── */}
      <SectionCard title="Active Shipments" viewAllPath="/shipments/dispatch">
        {activeShipments.length === 0 ?
        <EmptyState title="No active shipments" description="Active and in-transit shipments will appear here." /> :

        <ChartTableRow
          chart={
          <ReactApexChart
            type="donut"
            options={shipmentDonutOptions}
            series={shipmentCounts}
            height={200}
            width="100%" />

          }
          table={
          <DataTable
            columns={shipmentColumns}
            data={activeShipments}
            onRowClick={(row) => navigate(`/shipments/dispatch/${row.id}`)} />

          } />

        }
      </SectionCard>

      {/* ── Inventory Alerts ── */}
      <SectionCard title="Inventory Alerts" viewAllPath="/inventory">
        {inventoryAlerts.length === 0 ?
        <EmptyState title="No alerts" description="Low stock and out-of-stock alerts will appear here." /> :

        <ChartTableRow
          chart={
          <ReactApexChart
            type="bar"
            options={alertBarOptions}
            series={alertBarSeries}
            height={200}
            width="100%" />

          }
          table={
          <DataTable
            columns={alertColumns}
            data={inventoryAlerts}
            onRowClick={() => navigate('/inventory')} />

          } />

        }
      </SectionCard>
    </Box>);

}